import { useEffect, useRef, useState } from "react";
import { Form, FormikProvider, useFormik } from "formik";
import { EntityProfileIndustry, EntityProfileType } from "@common/types/entity";
import { AccountingLedgerBasis } from "@common/types/domains/accounting/ledger";
import {
    ChartOfAccountsTemplateCreateSchema,
    ChartOfAccountsTemplateHeaderSchema,
    ChartOfAccountsTemplateUpdateSchema,
} from "@common/schemas/chart-of-accounts-template.schema";
import { ChartOfAccountsTemplate } from "@common/types/chartOfAccountsTemplate";
import { ChartOfAccountsTemplateStartingPoint as ChartOfAccountsTemplateStartingPointEnum } from "@common/constants/domains/accounting/chartOfAccountsTemplateStartingPoint";
import {
    useChartOfAccountsTemplates,
    useCreateChartOfAccountsTemplateMutation,
    useUpdateChartOfAccountsTemplateMutation,
} from "@api/coaTemplate.api";
import { submitHelper, validateFormikWithZodSchema } from "@helpers/form";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { HStack } from "@design-system/HStack/HStack";
import { VStack } from "@design-system/VStack/VStack";
import { ChartOfAccountsTemplateStartingPoint } from "./ChartOfAccountsTemplateStartingPoint";
import { ChartOfAccountsTemplateHeaderFields } from "./ChartOfAccountsTemplateHeaderFields";
import { useCopyEntitySourcePrefill } from "./useCopyEntitySourcePrefill";
import {
    buildStartingPointPayload,
    defaultStartingPointSelection,
    isStartingPointSelectionComplete,
} from "./chartOfAccountsTemplateStartingPoint.utils";
import { ChartOfAccountsTemplateUploadFlow } from "./upload/ChartOfAccountsTemplateUploadFlow";
import styles from "./ChartOfAccountsTemplateModal.module.scss";

interface TemplateFormValues {
    name: string;
    description: string;
    entityLegalType: EntityProfileType | null;
    industries: EntityProfileIndustry[];
    basis: AccountingLedgerBasis[];
}

const fields = createFormikFields<TemplateFormValues>();

const buildTemplateHeader = (values: TemplateFormValues) => ({
    name: values.name.trim(),
    description: values.description.trim() || null,
    entityLegalType: values.entityLegalType,
    industries: values.industries,
    basis: values.basis,
});

const defaultBasis = [
    AccountingLedgerBasis.cash,
    AccountingLedgerBasis.accruals,
];

interface Props {
    show: boolean;
    template?: ChartOfAccountsTemplate;
    duplicateSource?: ChartOfAccountsTemplate;
    onHide: () => void;
    onCreated?: (template: ChartOfAccountsTemplate) => void;
}

export const ChartOfAccountsTemplateModal = ({
    show,
    template,
    duplicateSource,
    onHide,
    onCreated,
}: Props) => {
    const { toast: successToast, error: errorToast } = useToaster();
    const isEditMode = !!template;
    const saveFailureMessage = isEditMode
        ? "Failed to save changes"
        : "Failed to create template";
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [pendingEntityTypeChange, setPendingEntityTypeChange] =
        useState<TemplateFormValues | null>(null);
    const [startingPointSelection, setStartingPointSelection] = useState(
        defaultStartingPointSelection,
    );
    const [isUploadFlowOpen, setIsUploadFlowOpen] = useState(false);

    const isImportCoaSelected =
        startingPointSelection.startingPoint ===
        ChartOfAccountsTemplateStartingPointEnum.importCoa;

    const copyEntitySourceId =
        startingPointSelection.startingPoint ===
        ChartOfAccountsTemplateStartingPointEnum.copyEntity
            ? startingPointSelection.sourceEntityId
            : null;
    const {
        prefill: copyEntityPrefill,
        isHeaderReady: isCopyEntityHeaderReady,
        isBasisReady: isCopyEntityBasisReady,
    } = useCopyEntitySourcePrefill(copyEntitySourceId);
    // Separate guards: the header fields (legal type + industries) come from
    // the cached workspace list and apply immediately on selection; basis comes
    // from an async ledgers fetch and applies once resolved. Tracking them
    // independently keeps the basis round-trip from clobbering a manual header
    // edit, and lets each apply exactly once per selected entity.
    const prefilledHeaderEntityIdRef = useRef<number | null>(null);
    const prefilledBasisEntityIdRef = useRef<number | null>(null);
    // Whether the user manually edited Basis for the currently-selected source
    // entity during its (async) ledgers fetch. The basis prefill skips it so a
    // manual edit isn't clobbered when the fetch resolves. Reset on every source
    // change (tracked below) so it never leaks across entities.
    const basisManuallyEditedRef = useRef(false);
    const lastPrefillSourceIdRef = useRef<number | null>(null);

    const { data: templates } = useChartOfAccountsTemplates({
        enabled: isEditMode,
        includeArchived: true,
    });
    const isTemplateApplied =
        isEditMode &&
        (templates ?? []).some(
            (listedTemplate) =>
                listedTemplate.id === template?.id && listedTemplate.isApplied,
        );

    const createTemplate = useCreateChartOfAccountsTemplateMutation();
    const updateTemplate = useUpdateChartOfAccountsTemplateMutation(template);
    const isPending = isEditMode
        ? updateTemplate.isPending
        : createTemplate.isPending;

    const formik = useFormik<TemplateFormValues>({
        initialValues: {
            name:
                template?.name ??
                (duplicateSource ? `Copy of ${duplicateSource.name}` : ""),
            description:
                template?.description ?? duplicateSource?.description ?? "",
            entityLegalType:
                template?.entityLegalType ??
                duplicateSource?.entityLegalType ??
                null,
            industries:
                template?.industries ?? duplicateSource?.industries ?? [],
            basis: template?.basis ?? duplicateSource?.basis ?? defaultBasis,
        },
        enableReinitialize: true,
        validate: validateFormikWithZodSchema(
            ChartOfAccountsTemplateHeaderSchema,
        ),
        validateOnMount: false,
        validateOnBlur: false,
        validateOnChange: hasSubmitted,
        onSubmit: submitHelper({
            loading: isPending,
            handler: async (values) => {
                if (!isEditMode && isImportCoaSelected) {
                    setIsUploadFlowOpen(true);
                    return;
                }
                const isEntityTypeChanging =
                    isEditMode &&
                    !!template &&
                    !template.hasCustomChartOfAccounts &&
                    values.entityLegalType !== template.entityLegalType;
                if (isEntityTypeChanging) {
                    setPendingEntityTypeChange(values);
                    return;
                }
                await persist(values);
            },
            failureHandler: async () => {
                errorToast(saveFailureMessage);
            },
        }),
    });

    const persist = async (values: TemplateFormValues) => {
        const draft = buildTemplateHeader(values);
        if (isEditMode) {
            await updateTemplate.mutateAsync(
                ChartOfAccountsTemplateUpdateSchema.parse(draft),
            );
            successToast("Saved changes");
        } else {
            const startingPointPayload = buildStartingPointPayload(
                startingPointSelection,
            );
            if (!startingPointPayload) {
                return;
            }
            const payload = ChartOfAccountsTemplateCreateSchema.parse({
                ...draft,
                ...startingPointPayload,
            });
            const created = await createTemplate.mutateAsync(payload);
            successToast(`Created template "${payload.name}"`);
            onCreated?.(created);
        }
        formik.resetForm();
        setStartingPointSelection(defaultStartingPointSelection);
        onHide();
    };

    const { values, setFieldValue, submitCount } = formik;

    const uploadHeader = values.entityLegalType
        ? {
              ...buildTemplateHeader(values),
              entityLegalType: values.entityLegalType,
          }
        : null;
    const handleUploadFlowClose = () => {
        setIsUploadFlowOpen(false);
    };

    const handleUploadedTemplateCreated = (
        created: ChartOfAccountsTemplate,
    ) => {
        successToast(`Created template "${created.name}"`);
        setIsUploadFlowOpen(false);
        onCreated?.(created);
        handleHide();
    };

    useEffect(() => {
        if (submitCount > 0) {
            setHasSubmitted(true);
        }
    }, [submitCount]);

    useEffect(() => {
        if (!show) {
            setHasSubmitted(false);
            setPendingEntityTypeChange(null);
            prefilledHeaderEntityIdRef.current = null;
            prefilledBasisEntityIdRef.current = null;
            basisManuallyEditedRef.current = false;
            lastPrefillSourceIdRef.current = null;
        }
    }, [show]);

    useEffect(() => {
        if (show && duplicateSource) {
            setStartingPointSelection({
                startingPoint:
                    ChartOfAccountsTemplateStartingPointEnum.copyTemplate,
                sourceTemplateId: duplicateSource.id,
                sourceEntityId: null,
            });
        }
    }, [show, duplicateSource]);

    // Prefill the header fields from the chosen source entity when the starting
    // point is "Copy from entity". Each field group is guarded by entity id so
    // it applies once per selection (the user's later manual edits are not
    // overwritten); picking a different source entity re-prefills. Header
    // fields apply immediately from cache; basis applies once its fetch
    // resolves, without re-touching the header.
    useEffect(() => {
        // Whenever the selected source entity changes (including to/from none),
        // clear the per-selection manual-edit guard so it never suppresses a
        // legitimate re-prefill for a different (or revisited) entity.
        if (lastPrefillSourceIdRef.current !== copyEntitySourceId) {
            lastPrefillSourceIdRef.current = copyEntitySourceId;
            basisManuallyEditedRef.current = false;
        }
        if (copyEntitySourceId === null) {
            prefilledHeaderEntityIdRef.current = null;
            prefilledBasisEntityIdRef.current = null;
            return;
        }
        if (!copyEntityPrefill) {
            return;
        }
        if (
            isCopyEntityHeaderReady &&
            prefilledHeaderEntityIdRef.current !== copyEntitySourceId
        ) {
            prefilledHeaderEntityIdRef.current = copyEntitySourceId;
            void setFieldValue(
                "entityLegalType",
                copyEntityPrefill.entityLegalType,
            );
            void setFieldValue("industries", copyEntityPrefill.industries);
        }
        if (
            isCopyEntityBasisReady &&
            prefilledBasisEntityIdRef.current !== copyEntitySourceId
        ) {
            prefilledBasisEntityIdRef.current = copyEntitySourceId;
            // Don't clobber a Basis the user edited while the ledgers fetch was
            // still in flight for this entity.
            if (!basisManuallyEditedRef.current) {
                void setFieldValue("basis", copyEntityPrefill.basis);
            }
        }
    }, [
        copyEntitySourceId,
        isCopyEntityHeaderReady,
        isCopyEntityBasisReady,
        copyEntityPrefill,
        setFieldValue,
    ]);

    const handleHide = () => {
        formik.resetForm();
        setStartingPointSelection(defaultStartingPointSelection);
        setPendingEntityTypeChange(null);
        onHide();
    };

    const isStartingPointReady =
        isEditMode ||
        isImportCoaSelected ||
        isStartingPointSelectionComplete(startingPointSelection);

    // Required fields must be filled before the primary CTA is enabled. This
    // is intentionally decoupled from formik.isValid, which starts out "valid"
    // (validation is deferred until submit) and would otherwise enable the
    // button on an empty form.
    const areRequiredFieldsFilled =
        values.name.trim().length > 0 && values.entityLegalType !== null;
    const canSubmit = areRequiredFieldsFilled && isStartingPointReady;
    const createLabel = isImportCoaSelected ? "Continue" : "Create";
    const submitLabel = isEditMode ? "Save changes" : createLabel;

    return (
        <>
            <Modal
                show={show && !isUploadFlowOpen}
                onClose={handleHide}
                size="lg"
            >
                <StandardModalHeader>
                    {isEditMode
                        ? "Edit chart of accounts template"
                        : "Create template"}
                </StandardModalHeader>
                <FormikProvider value={formik}>
                    <Form className={styles.form}>
                        <StandardModalBody>
                            <VStack gap={20}>
                                <fields.Text
                                    name="name"
                                    label="Template name"
                                    size="lg"
                                    placeholder="e.g. Manufacturing · S-Corp"
                                />

                                <fields.TextArea
                                    name="description"
                                    label="Description"
                                    optional
                                    size="lg"
                                    autoGrow
                                    minRows={2}
                                    placeholder="What is this template for?"
                                />

                                <ChartOfAccountsTemplateHeaderFields
                                    entityLegalType={values.entityLegalType}
                                    isEntityTypeLocked={isTemplateApplied}
                                    industries={values.industries}
                                    basis={values.basis}
                                    onEntityTypeChange={(value) => {
                                        void setFieldValue(
                                            "entityLegalType",
                                            value,
                                        );
                                    }}
                                    onIndustriesChange={(value) => {
                                        void setFieldValue("industries", value);
                                    }}
                                    onBasisChange={(value) => {
                                        basisManuallyEditedRef.current = true;
                                        void setFieldValue("basis", value);
                                    }}
                                />

                                {!isEditMode && (
                                    <ChartOfAccountsTemplateStartingPoint
                                        value={startingPointSelection}
                                        onChange={setStartingPointSelection}
                                        duplicateSource={duplicateSource}
                                    />
                                )}
                            </VStack>
                        </StandardModalBody>
                        <StandardModalFooter>
                            <HStack gap={12} justify="end">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    size="xl"
                                    onClick={handleHide}
                                    disabled={isPending}
                                >
                                    Cancel
                                </Button>
                                <ButtonWithLoader
                                    type="submit"
                                    variant="primary"
                                    size="xl"
                                    loading={isPending}
                                    disabled={!canSubmit}
                                    hotKey="mod+enter"
                                    hotKeyOptions={{
                                        description: isEditMode
                                            ? "Save template"
                                            : "Create template",
                                        preventDefault: true,
                                        enableOnFormTags: true,
                                    }}
                                >
                                    {submitLabel}
                                </ButtonWithLoader>
                            </HStack>
                        </StandardModalFooter>
                    </Form>
                </FormikProvider>
            </Modal>

            {isUploadFlowOpen && uploadHeader && (
                <ChartOfAccountsTemplateUploadFlow
                    header={uploadHeader}
                    onClose={handleUploadFlowClose}
                    onCreated={handleUploadedTemplateCreated}
                />
            )}

            <ConfirmationModal
                show={pendingEntityTypeChange !== null}
                title="Change entity type?"
                question={
                    <>
                        Kick will swap this template&apos;s{" "}
                        <strong>default equity accounts</strong> to match the
                        new entity type (for example, Owner&apos;s Equity
                        becomes Common Stock and Retained Earnings for an S
                        Corporation).{" "}
                        <strong>Your custom accounts won&apos;t change.</strong>
                    </>
                }
                yes="Change and save"
                no="Cancel"
                onReject={() => setPendingEntityTypeChange(null)}
                onConfirm={async () => {
                    const pendingValues = pendingEntityTypeChange;
                    if (!pendingValues) {
                        return;
                    }
                    try {
                        await persist(pendingValues);
                        setPendingEntityTypeChange(null);
                    } catch {
                        errorToast(saveFailureMessage);
                    }
                }}
            />
        </>
    );
};
